import { AcademyReceiptsBoundary } from '../components/StudentPaymentReceipts';
import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

export default function InvoiceDetail() {
  const companyId = localStorage.getItem('selectedCompanyId');
  return <AcademyReceiptsBoundary companyId={companyId}><GenericInvoiceDetail /></AcademyReceiptsBoundary>;
}

function GenericInvoiceDetail() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);

  // Receipt modal
  const [showReceipt, setShowReceipt] = useState(false);
  const [receiptForm, setReceiptForm] = useState({ amount: '', payment_date: new Date().toISOString().split('T')[0], payment_method: '', notes: '' });
  const [receiptSaving, setReceiptSaving] = useState(false);

  // Cancel modal
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Edit mode
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editItems, setEditItems] = useState([]);
  const [editSaving, setEditSaving] = useState(false);

  const fetchInvoice = () => {
    if (!companyId || !invoiceId) return;
    setLoading(true);
    fetch(`${API}/companies/${companyId}/invoicing/invoices/${invoiceId}`, { headers: getAuthHeaders() })
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => {
        setInvoice(d.invoice);
        setReceiptForm(f => ({ ...f, amount: d.invoice.remaining_amount }));
      })
      .catch(() => setInvoice(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchInvoice(); }, [companyId, invoiceId]);

  const handleIssue = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/invoicing/invoices/${invoiceId}/issue`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || 'Failed'); }
      fetchInvoice();
    } catch (e) { alert(e.message); }
  };

  const handleCancel = async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/invoicing/invoices/${invoiceId}/cancel`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason }),
      });
      if (!res.ok) throw new Error();
      setShowCancel(false);
      fetchInvoice();
    } catch { alert('Failed to cancel invoice'); }
  };

  const handleCreateReceipt = async () => {
    setReceiptSaving(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/invoicing/receipts`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoice_id: parseInt(invoiceId), ...receiptForm, amount: parseFloat(receiptForm.amount) }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.message || 'Failed'); }
      setShowReceipt(false);
      fetchInvoice();
    } catch (e) { alert(e.message || 'Failed to create receipt'); }
    finally { setReceiptSaving(false); }
  };

  const handleCancelReceipt = async (receiptId) => {
    if (!window.confirm('Cancel this receipt? This will reverse the payment amount.')) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/invoicing/receipts/${receiptId}/cancel`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Cancelled from invoice detail' }),
      });
      if (!res.ok) throw new Error();
      fetchInvoice();
    } catch { alert('Failed to cancel receipt'); }
  };

  const startEdit = () => {
    setEditForm({
      customer_name: invoice.customer_name || '',
      customer_organization: invoice.customer_organization || '',
      customer_address: invoice.customer_address || '',
      customer_phone: invoice.customer_phone || '',
      customer_email: invoice.customer_email || '',
      invoice_date: invoice.invoice_date ? invoice.invoice_date.split('T')[0] : '',
      due_date: invoice.due_date ? invoice.due_date.split('T')[0] : '',
      tax_name: invoice.tax_name || '',
      tax_percentage: invoice.tax_percentage || 0,
      discount_percentage: invoice.discount_percentage || 0,
      discount_amount: invoice.discount_amount || 0,
      notes: invoice.notes || '',
      next_payment_amount: invoice.next_payment_amount || '',
      next_payment_date: invoice.next_payment_date ? invoice.next_payment_date.split('T')[0] : '',
    });
    setEditItems(Array.isArray(invoice.items) ? invoice.items.map(it => ({
      description: it.description, quantity: it.quantity, unit_price: it.unit_price, service_id: it.service_id,
    })) : []);
    setEditing(true);
  };

  const handleSave = async () => {
    setEditSaving(true);
    try {
      const payload = { ...editForm, items: editItems };
      if (invoice.document_status === 'issued') {
        payload.revision_reason = 'Edited from invoice detail';
      }
      const res = await fetch(`${API}/companies/${companyId}/invoicing/invoices/${invoiceId}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setEditing(false);
      fetchInvoice();
    } catch { alert('Failed to update invoice'); }
    finally { setEditSaving(false); }
  };

  const addEditItem = () => setEditItems([...editItems, { description: '', quantity: 1, unit_price: 0 }]);
  const removeEditItem = (idx) => { if (editItems.length > 1) setEditItems(editItems.filter((_, i) => i !== idx)); };
  const updateEditItem = (idx, field, val) => {
    const updated = [...editItems];
    updated[idx] = { ...updated[idx], [field]: val };
    setEditItems(updated);
  };

  const fmt = (v) => parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (!companyId) return <div style={pageStyle}><p>Please select a company first.</p></div>;
  if (initialLoading) return <div style={pageStyle}><p>Loading...</p></div>;
  if (!invoice) return <div style={pageStyle}><p>Invoice not found.</p></div>;

  const inv = invoice;
  const receipts = Array.isArray(inv.receipts) ? inv.receipts : [];
  const versions = Array.isArray(inv.versions) ? inv.versions : [];
  const items = Array.isArray(inv.items) ? inv.items : [];
  const isUnsettled = inv.document_status === 'issued' && inv.remaining_amount > 0;
  const isOverdue = isUnsettled && inv.due_date && new Date(inv.due_date) < new Date();

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
        <div>
          <button onClick={() => navigate('/company/invoicing/invoices')} style={linkBtnStyle}>← Back to Invoices</button>
          <h2 style={{ margin: '4px 0 0', fontSize: '1.4rem', fontWeight: 700 }}>
            {inv.invoice_number}
            {inv.is_revised && <span style={{ fontSize: '0.75rem', color: '#f59e0b', marginLeft: '8px' }}>✏️ Revised (v{inv.version})</span>}
          </h2>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {inv.document_status === 'draft' && (
            <>
              <button onClick={handleIssue} style={{ ...btnPrimary, background: '#10b981' }}>Issue Invoice</button>
              <button onClick={startEdit} style={btnSecondary}>Edit</button>
            </>
          )}
          {inv.document_status === 'issued' && (
            <>
              {isUnsettled && <button onClick={() => { setReceiptForm({ amount: inv.remaining_amount, payment_date: new Date().toISOString().split('T')[0], payment_method: '', notes: '' }); setShowReceipt(true); }} style={{ ...btnPrimary, background: '#10b981' }}>Create Receipt</button>}
              <button onClick={startEdit} style={btnSecondary}>Edit / Revise</button>
              <button onClick={() => setShowCancel(true)} style={{ ...btnSecondary, color: '#ef4444' }}>Cancel</button>
            </>
          )}
        </div>
      </div>

      {/* Status Badges */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <span style={{ ...statusBadge, background: docStatusColors[inv.document_status]?.bg || '#f1f5f9', color: docStatusColors[inv.document_status]?.text || '#64748b' }}>
          {inv.document_status}
        </span>
        <span style={{ ...statusBadge, background: payStatusColors[inv.payment_status]?.bg || '#f1f5f9', color: payStatusColors[inv.payment_status]?.text || '#64748b' }}>
          {(inv.payment_status || '').replace(/_/g, ' ')}
        </span>
        {isOverdue && <span style={{ ...statusBadge, background: '#fef2f2', color: '#dc2626' }}>⚠ OVERDUE</span>}
      </div>

      {/* Edit Mode */}
      {editing ? (
        <div style={{ ...cardStyle, padding: '20px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 16px', fontWeight: 600 }}>Edit Invoice</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div><label style={labelStyle}>Customer Name</label><input value={editForm.customer_name} onChange={e => setEditForm({ ...editForm, customer_name: e.target.value })} style={modalInputStyle} /></div>
            <div><label style={labelStyle}>Organization</label><input value={editForm.customer_organization} onChange={e => setEditForm({ ...editForm, customer_organization: e.target.value })} style={modalInputStyle} /></div>
            <div><label style={labelStyle}>Invoice Date</label><input type="date" value={editForm.invoice_date} onChange={e => setEditForm({ ...editForm, invoice_date: e.target.value })} style={modalInputStyle} /></div>
            <div><label style={labelStyle}>Due Date</label><input type="date" value={editForm.due_date} onChange={e => setEditForm({ ...editForm, due_date: e.target.value })} style={modalInputStyle} /></div>
            <div><label style={labelStyle}>Tax Name</label><input value={editForm.tax_name} onChange={e => setEditForm({ ...editForm, tax_name: e.target.value })} style={modalInputStyle} /></div>
            <div><label style={labelStyle}>Tax %</label><input type="number" value={editForm.tax_percentage} onChange={e => setEditForm({ ...editForm, tax_percentage: e.target.value })} style={modalInputStyle} /></div>
            <div><label style={labelStyle}>Discount %</label><input type="number" value={editForm.discount_percentage} onChange={e => setEditForm({ ...editForm, discount_percentage: e.target.value })} style={modalInputStyle} /></div>
            <div><label style={labelStyle}>Discount Amount</label><input type="number" value={editForm.discount_amount} onChange={e => setEditForm({ ...editForm, discount_amount: e.target.value })} style={modalInputStyle} /></div>
          </div>
          <h4 style={{ margin: '16px 0 8px', fontSize: '0.9rem', fontWeight: 600 }}>Line Items</h4>
          {editItems.map((item, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '8px', marginBottom: '6px' }}>
              <input placeholder="Description" value={item.description} onChange={e => updateEditItem(idx, 'description', e.target.value)} style={modalInputStyle} />
              <input type="number" placeholder="Qty" value={item.quantity} onChange={e => updateEditItem(idx, 'quantity', e.target.value)} style={modalInputStyle} />
              <input type="number" placeholder="Price" value={item.unit_price} onChange={e => updateEditItem(idx, 'unit_price', e.target.value)} style={modalInputStyle} />
              <button onClick={() => removeEditItem(idx)} style={{ ...btnCancel, padding: '4px 8px' }}>✕</button>
            </div>
          ))}
          <button onClick={addEditItem} style={{ ...linkBtnStyle, marginBottom: '12px' }}>+ Add Item</button>
          <div><label style={labelStyle}>Notes</label><textarea value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} style={{ ...modalInputStyle, minHeight: '50px' }} /></div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '16px', justifyContent: 'flex-end' }}>
            <button onClick={() => setEditing(false)} style={btnCancel}>Cancel</button>
            <button onClick={handleSave} disabled={editSaving} style={btnPrimary}>{editSaving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </div>
      ) : (
        <>
          {/* Invoice Info Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            {/* Bill From / Bill To */}
            <div style={{ ...cardStyle, padding: '16px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>BILL FROM</h4>
              <div style={{ fontSize: '0.9rem', color: '#334155' }}>
                <div style={{ fontWeight: 600 }}>{inv.issuer_name || '—'}</div>
                {inv.issuer_address && <div>{inv.issuer_address}</div>}
                {inv.issuer_phone && <div>{inv.issuer_phone}</div>}
                {inv.issuer_email && <div>{inv.issuer_email}</div>}
              </div>
            </div>
            <div style={{ ...cardStyle, padding: '16px' }}>
              <h4 style={{ margin: '0 0 8px', fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>BILL TO</h4>
              <div style={{ fontSize: '0.9rem', color: '#334155' }}>
                <div style={{ fontWeight: 600 }}>{inv.customer_name}</div>
                {inv.customer_organization && <div>{inv.customer_organization}</div>}
                {inv.customer_address && <div>{inv.customer_address}</div>}
                {inv.customer_phone && <div>{inv.customer_phone}</div>}
                {inv.customer_email && <div>{inv.customer_email}</div>}
                {inv.billing_contact_name && <div style={{ fontSize: '0.8rem', color: '#64748b' }}>Contact: {inv.billing_contact_name}</div>}
              </div>
              {inv.client_id && <button onClick={() => navigate(`/company/clients/${inv.client_id}`)} style={{ ...linkBtnStyle, marginTop: '8px' }}>Open Client →</button>}
            </div>
          </div>

          {/* Dates + Financial Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div style={{ ...cardStyle, padding: '16px' }}>
              <h4 style={{ margin: '0 0 10px', fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>DETAILS</h4>
              <InfoRow label="Invoice Date" value={inv.invoice_date ? new Date(inv.invoice_date).toLocaleDateString() : '—'} />
              <InfoRow label="Due Date" value={inv.due_date ? new Date(inv.due_date).toLocaleDateString() : '—'} />
              <InfoRow label="Currency" value={inv.currency} />
              {inv.source_module && inv.source_module !== 'invoicing' && <InfoRow label="Created From" value={inv.source_module.replace(/_/g, ' ')} />}
              {inv.created_by_user && <InfoRow label="Created By" value={inv.created_by_user.name} />}
              {inv.account_owner && <InfoRow label="Account Owner" value={inv.account_owner.name} />}
            </div>
            <div style={{ ...cardStyle, padding: '16px' }}>
              <h4 style={{ margin: '0 0 10px', fontSize: '0.85rem', fontWeight: 600, color: '#64748b' }}>FINANCIAL</h4>
              <InfoRow label="Subtotal" value={fmt(inv.subtotal)} />
              {parseFloat(inv.discount_amount) > 0 && <InfoRow label={`Discount${inv.discount_percentage > 0 ? ` (${inv.discount_percentage}%)` : ''}`} value={'-' + fmt(inv.discount_amount)} color="#ef4444" />}
              {parseFloat(inv.tax_amount) > 0 && <InfoRow label={`Tax${inv.tax_name ? ` (${inv.tax_name})` : ''}`} value={fmt(inv.tax_amount)} />}
              <div style={{ borderTop: '2px solid #e2e8f0', marginTop: '6px', paddingTop: '6px' }}>
                <InfoRow label="Total" value={fmt(inv.total)} bold />
                <InfoRow label="Paid" value={fmt(inv.paid_amount)} color="#10b981" />
                <InfoRow label="Remaining" value={fmt(inv.remaining_amount)} color={inv.remaining_amount > 0 ? '#ef4444' : '#10b981'} bold />
              </div>
              {inv.next_payment_amount && <InfoRow label="Next Payment" value={`${fmt(inv.next_payment_amount)} on ${inv.next_payment_date ? new Date(inv.next_payment_date).toLocaleDateString() : '—'}`} />}
            </div>
          </div>

          {/* Line Items */}
          <div style={{ ...cardStyle, padding: '16px', marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>📦 Line Items</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>#</th>
                    <th style={thStyle}>Description</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Qty</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Unit Price</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={it.id || i}>
                      <td style={tdStyle}>{i + 1}</td>
                      <td style={tdStyle}>{it.description}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{parseFloat(it.quantity)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{fmt(it.unit_price)}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{fmt(it.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Notes */}
          {inv.notes && (
            <div style={{ ...cardStyle, padding: '16px', marginBottom: '20px' }}>
              <h3 style={{ margin: '0 0 8px', fontSize: '1rem', fontWeight: 600 }}>📝 Notes</h3>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#334155', whiteSpace: 'pre-wrap' }}>{inv.notes}</p>
            </div>
          )}
        </>
      )}

      {/* Settlement History (Receipts) */}
      <div style={{ ...cardStyle, padding: '16px', marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>💳 Settlement History</h3>
        {receipts.length === 0 ? (
          <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: 0 }}>No receipts recorded yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Receipt #</th>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Amount</th>
                  <th style={thStyle}>Method</th>
                  <th style={thStyle}>Remaining After</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {receipts.map(rec => (
                  <tr key={rec.id}>
                    <td style={tdStyle}><span style={{ fontWeight: 600, color: rec.status === 'cancelled' ? '#94a3b8' : '#10b981' }}>{rec.receipt_number}</span></td>
                    <td style={tdStyle}>{rec.payment_date ? new Date(rec.payment_date).toLocaleDateString() : '—'}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, textDecoration: rec.status === 'cancelled' ? 'line-through' : 'none' }}>{fmt(rec.amount)}</td>
                    <td style={tdStyle}>{rec.payment_method || '—'}</td>
                    <td style={tdStyle}>{fmt(rec.remaining_after)}</td>
                    <td style={tdStyle}>
                      <span style={{ ...badgeStyle, background: rec.status === 'issued' ? '#dcfce7' : '#fef2f2', color: rec.status === 'issued' ? '#16a34a' : '#dc2626' }}>
                        {rec.status}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {rec.status === 'issued' && (
                        <button onClick={() => handleCancelReceipt(rec.id)} style={{ ...linkBtnStyle, color: '#ef4444', fontSize: '0.75rem' }}>Cancel</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Version History */}
      {versions.length > 0 && (
        <div style={{ ...cardStyle, padding: '16px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>📜 Version History</h3>
          {versions.map(v => (
            <div key={v.id} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                <span style={{ fontWeight: 600 }}>Version {v.version_number}</span>
                <span style={{ color: '#64748b' }}>{v.edited_by_user?.name || 'System'} • {new Date(v.created_at).toLocaleString()}</span>
              </div>
              {v.reason && <div style={{ fontSize: '0.8rem', color: '#475569', marginTop: '2px' }}>Reason: {v.reason}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Source Links */}
      {(inv.related_project_id || inv.related_service_id || inv.source_module) && (
        <div style={{ ...cardStyle, padding: '16px', marginBottom: '20px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '1rem', fontWeight: 600 }}>🔗 Source References</h3>
          {inv.source_module && <InfoRow label="Source Module" value={(inv.source_module || '').replace(/_/g, ' ')} />}
          {inv.related_project_id && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Related Project</span>
              <button onClick={() => navigate(`/company/projects/${inv.related_project_id}`)} style={linkBtnStyle}>Open Project →</button>
            </div>
          )}
          {inv.related_service_id && <InfoRow label="Related Service ID" value={inv.related_service_id} />}
          {inv.related_department_id && <InfoRow label="Department ID" value={inv.related_department_id} />}
        </div>
      )}

      {/* Receipt Modal */}
      {showReceipt && (
        <div style={overlayStyle}>
          <div style={modalBoxStyle}>
            <h3 style={{ margin: '0 0 16px', fontWeight: 700 }}>Create Receipt</h3>
            <p style={{ fontSize: '0.85rem', color: '#64748b', margin: '0 0 12px' }}>
              Invoice: <strong>{inv.invoice_number}</strong> • Remaining: <strong>{fmt(inv.remaining_amount)}</strong>
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div><label style={labelStyle}>Amount *</label><input type="number" value={receiptForm.amount} onChange={e => setReceiptForm({ ...receiptForm, amount: e.target.value })} style={modalInputStyle} step="any" min="0.01" max={inv.remaining_amount} /></div>
              <div><label style={labelStyle}>Payment Date *</label><input type="date" value={receiptForm.payment_date} onChange={e => setReceiptForm({ ...receiptForm, payment_date: e.target.value })} style={modalInputStyle} /></div>
              <div><label style={labelStyle}>Payment Method</label>
                <select value={receiptForm.payment_method} onChange={e => setReceiptForm({ ...receiptForm, payment_method: e.target.value })} style={modalInputStyle}>
                  <option value="">Select...</option>
                  <option value="cash">Cash</option>
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="check">Check</option>
                  <option value="credit_card">Credit Card</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div><label style={labelStyle}>Notes</label><textarea value={receiptForm.notes} onChange={e => setReceiptForm({ ...receiptForm, notes: e.target.value })} style={{ ...modalInputStyle, minHeight: '50px' }} /></div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowReceipt(false)} style={btnCancel}>Cancel</button>
              <button onClick={handleCreateReceipt} disabled={receiptSaving || !receiptForm.amount || !receiptForm.payment_date} style={{ ...btnPrimary, background: '#10b981' }}>
                {receiptSaving ? 'Creating...' : 'Create Receipt'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancel && (
        <div style={overlayStyle}>
          <div style={modalBoxStyle}>
            <h3 style={{ margin: '0 0 16px', fontWeight: 700, color: '#ef4444' }}>Cancel Invoice</h3>
            <p style={{ fontSize: '0.85rem', color: '#475569', margin: '0 0 12px' }}>
              Are you sure you want to cancel <strong>{inv.invoice_number}</strong>? The number will be preserved.
            </p>
            <div><label style={labelStyle}>Reason</label><textarea value={cancelReason} onChange={e => setCancelReason(e.target.value)} style={{ ...modalInputStyle, minHeight: '60px' }} placeholder="Reason for cancellation..." /></div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowCancel(false)} style={btnCancel}>Back</button>
              <button onClick={handleCancel} style={{ ...btnPrimary, background: '#ef4444' }}>Confirm Cancellation</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, color, bold }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '0.85rem' }}>
      <span style={{ color: '#64748b' }}>{label}</span>
      <span style={{ color: color || '#334155', fontWeight: bold ? 700 : 400 }}>{value}</span>
    </div>
  );
}

const docStatusColors = {
  draft: { bg: '#f1f5f9', text: '#64748b' },
  issued: { bg: '#dbeafe', text: '#2563eb' },
  cancelled: { bg: '#fef2f2', text: '#dc2626' },
};
const payStatusColors = {
  unpaid: { bg: '#fef2f2', text: '#dc2626' },
  partially_paid: { bg: '#fef3c7', text: '#d97706' },
  paid: { bg: '#dcfce7', text: '#16a34a' },
};

const pageStyle = { padding: '24px', maxWidth: '1000px' };
const cardStyle = { background: '#fff', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' };
const statusBadge = { padding: '4px 12px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 600, textTransform: 'capitalize' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' };
const thStyle = { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase' };
const tdStyle = { padding: '10px', borderBottom: '1px solid #f1f5f9', color: '#334155' };
const badgeStyle = { padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 600, textTransform: 'capitalize' };
const btnPrimary = { padding: '8px 16px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' };
const btnCancel = { padding: '8px 16px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '0.85rem', cursor: 'pointer' };
const linkBtnStyle = { background: 'none', border: 'none', color: '#3b82f6', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: '0' };
const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalBoxStyle = { background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '500px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' };
const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '4px' };
const modalInputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box' };
